import { CurrencyPipe, NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PasAccordionBodyDirective,
  PasAccordionComponent,
  PasButtonComponent,
  PasConfirmationService,
  PasDateTimeComponent,
  PasFormValidationMessage,
  PasInputFieldComponent,
  PasInputNumberComponent,
  PasLayoutSidebarHolderComponent,
  PasRadioButtonComponent,
  PasSelectComponent,
  PasSidebarHandleService,
  PasSpinnerComponent,
  PasSystemProcessComponent,
  PasTextareaComponent,
} from '@porsche-aftersales/frontend-toolbox';
import { AgGridAngular } from 'ag-grid-angular';
import { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import * as fileSaver from 'file-saver';
import { cloneDeep, isEmpty, isEqual, isNil } from 'lodash-es';
import { Subscription } from 'rxjs';

import { SupplierService } from '../../../api/tur-rc/services/supplier.service';
import { DecimalFormatConverter, UserLanguage } from '../../../core/decimal-format-converter';
import { DateFormatPipe } from '../../../pipes/date-format.pipe';
import { AGGridFormatterService } from '../../../services/ag-grid/formatter-service';
import { AttachmentService } from '../../../services/attachment/attachment.service';
import {
  newFormDataForAttachment,
  showAttachmentDeleteFailed,
  showAttachmentDeleteSuccess,
  showAttachmentDownloadFailed,
  showAttachmentUploadFailed,
  showAttachmentUploadSuccess,
} from '../../../services/booking/recourse-booking-functions';
import { RecourseBookingStore } from '../../../services/booking/recourse-booking-store';
import { BookingRoutingService } from '../../../services/details-view-service/booking-routing.service';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { RecourseCampaignStore } from '../../../services/recourse-campaign/recourse-campaign-store.service';
import { RecourseCampaignService } from '../../../services/recourse-campaign/recourse-campaign.service';
import { SupplierModel } from '../../../services/supplier-service/model/supplier.model';
import { UserRights } from '../../../services/user-service/user-rights.enum';
import { UserService } from '../../../services/user-service/user.service';
import { setGridHeightConditionally } from '../../../shared/ag-grid/ag-grid-functions';
import { AgGridService } from '../../../shared/ag-grid/ag-grid.service';
import { BookingStateEnum } from '../../../shared/enums/booking-state-enum';
import { BookingTypeEnum } from '../../../shared/enums/booking-type-enum';
import { CampaignStateEnum } from '../../../shared/enums/campaign-state-enum';
import { extractEnumValues, getEnumStringValue } from '../../../shared/enums/enum-util-functions/enum-utils';
import { RecourseTypeIdEnum } from '../../../shared/enums/recourse-type-id-enum';
import { TopicEnum } from '../../../shared/enums/topic-enum';
import { GenericModalContentComponent } from '../../../shared/generic-modal-content/generic-modal-content.component';
import { getDropdownValues } from '../../../shared/model-serializer/mapper-functions';
import { AttachmentModel } from '../../recourse-booking/model/attachment.model';
import { RecourseBookingModel } from '../../recourse-booking/model/recourse-booking-model';
import { UploadFileModel } from '../../recourse-booking/model/upload-file-model';
import { MAX_FILE_UPLOAD_SIZE } from '../../recourse-booking/recourse-booking-add-edit-details/recourse-booking-add-edit-details.component';
import { DownloadCellRenderer } from '../../recourse-booking/renderer/download-cell-renderer/download-cell-renderer.component';
import { RecourseCampaignDetailsModel } from '../model/recourse-campaign-details-model';
import { RecourseCampaignNegotiationModel } from '../model/recourse-campaign-negotiation-model';
import { ButtonDetailsRendererComponent } from '../renderer/button-details-renderer.component';
import { ButtonEditRendererComponent } from '../renderer/button-edit-renderer.component';
import { DeleteCellRendererComponent } from '../renderer/delete-cell-renderer/delete-cell-renderer.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'recourse-campaign-details',
  templateUrl: './recourse-campaign-details.component.html',
  styleUrls: ['./recourse-campaign-details.component.scss'],
  imports: [
    ReactiveFormsModule,
    PasSystemProcessComponent,
    TranslatePipe,
    CurrencyPipe,
    PasSelectComponent,
    PasAccordionComponent,
    PasInputFieldComponent,
    PasInputNumberComponent,
    PasDateTimeComponent,
    AgGridAngular,
    PasRadioButtonComponent,
    PasButtonComponent,
    PasTextareaComponent,
    NgClass,
    PasLayoutSidebarHolderComponent,
    GenericModalContentComponent,
    FormsModule,
    PasSpinnerComponent,
    PasAccordionBodyDirective,
  ],
})
export class RecourseCampaignDetailsComponent implements OnInit {
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly pasConfirmationService = inject(PasConfirmationService);
  private readonly translateService = inject(TranslateService);
  public readonly dateFormatPipe = inject(DateFormatPipe);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  public readonly recourseCampaingStore = inject(RecourseCampaignStore);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly userService = inject(UserService);
  private readonly bookingRoutingService = inject(BookingRoutingService);
  private readonly modalService = inject(NgbModal);
  private readonly agGridFormatterService = inject(AGGridFormatterService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly agGridService = inject(AgGridService);
  private readonly recourseCampaignService = inject(RecourseCampaignService);
  private readonly recourseBookingStore = inject(RecourseBookingStore);
  private readonly supplierService = inject(SupplierService);

  /* eslint-disable @angular-eslint/no-output-on-prefix */
  @Output('onBtnRecourseCampaignEditCancel') public onBtnRecourseCampaignEditCancelEE = new EventEmitter();
  @Output('onCheckRecourseCampaignEditAssignedPerson')
  public onCheckRecourseCampaignEditAssignedPersonEE: EventEmitter<any> = new EventEmitter();
  /* eslint-enable */

  public recourseCampaignEditForm: UntypedFormGroup;
  public generalDataColumnDefs: any;
  public generalDataRowData: any;
  public bookingTableColumnDefs: any;
  public generalAttachmentsGridOptions: GridOptions;
  public complaintAttachmentsGridOptions: GridOptions;
  public recourseClaimAttachmentsGridOptions: GridOptions;
  public negotiationAttachmentsGridOptions: GridOptions;
  public bookingGridOptions: GridOptions;
  public bookingTableRowData: any;
  public complaintTableColumnDefs: any;
  public complaintTableRowData: any;
  public recourseClaimTableColumnDefs: any;
  public recourseClaimTableRowData: any;
  public recourseNegotiationTableColumnDefs: any;
  public recourseNegotiationTableRowData: any;
  public defaultColDef: any;
  public multiSortKey: any;
  public bookingGridApi: any;
  public frameworkComponents: any;
  public generalDataGridApi: GridApi;
  public complaintGridApi: any;
  public recourseClaimGridApi: any;
  public negotiationGridApi: any;
  public validationMessages: PasFormValidationMessage[];
  public initialFormValue: any;
  public data: RecourseCampaignDetailsModel;
  public formValueChanged = false;
  public decimalSeparator: ',' | '.' = '.';
  public userLanguage: '' | 'DE';
  public isAssignedPersonAvailable = true;
  public defaultNegotiationDates: string;
  public selectedNegotiationDate: RecourseCampaignNegotiationModel;
  public externalReceiverCheckboxDisabled = false;
  public isCancelledOrInternalFault = false;

  @ViewChild('UploadFileDialogTemplate')
  public uploadFileDialogTemplate: ElementRef;
  public uploadFileDialog: NgbModalRef;
  public acceptanceAllowed = false;
  public topicDropdownValues: any[] = [];
  public uploadFileModel: UploadFileModel = new UploadFileModel();

  @ViewChild('processComponent')
  private readonly processComponent: PasSystemProcessComponent;
  public readonly maxFileUploadSize: number = MAX_FILE_UPLOAD_SIZE;
  public selectedRecourseCampaignState: any;

  @ViewChild('DuplicateCampaignDialogTemplate')
  public duplicateCampaignDialogTemplate: ElementRef;
  public duplicateCampaignDialog: NgbModalRef;
  public operationId = '';
  public spinnerActive = false;
  private formChangedSubscription: Subscription;

  protected readonly hasRightsForPartsRequestButton: boolean;

  public constructor() {
    this.frameworkComponents = {
      buttonEditRenderer: ButtonEditRendererComponent,
      buttonDetailsRenderer: ButtonDetailsRendererComponent,
      downloadCellRenderer: DownloadCellRenderer,
      deleteCellRenderer: DeleteCellRendererComponent,
    };
    this.hasRightsForPartsRequestButton =
      this.userService.hasRight(UserRights.CAMPAIGNS_WORKFLOW) && this.userService.hasRight(UserRights.QPP_SYS_RESERVE);
  }

  public ngOnInit(): void {
    this.initializeView();
  }

  public initializeView(): void {
    this.data = this.recourseCampaingStore.selectedRecourseCampaignEdit;
    this.defaultNegotiationDates =
      this.data.recourseCampaignNegotiations == null ? '' : JSON.stringify(this.data.recourseCampaignNegotiations);
    this.generalAttachmentsGridOptions = this.getCommonAttachmentsGridOptions();
    this.generalAttachmentsGridOptions.onRowDataUpdated = () => this.setGeneralAttachmentsGridHeight();
    this.complaintAttachmentsGridOptions = this.getCommonAttachmentsGridOptions();
    this.complaintAttachmentsGridOptions.onRowDataUpdated = () => this.setComplaintAttachmentsGridHeight();
    this.recourseClaimAttachmentsGridOptions = this.getCommonAttachmentsGridOptions();
    this.recourseClaimAttachmentsGridOptions.onRowDataUpdated = () => this.setRecourseClaimAttachmentsGridHeight();
    this.negotiationAttachmentsGridOptions = this.getCommonAttachmentsGridOptions();
    this.negotiationAttachmentsGridOptions.onRowDataUpdated = () => this.setNegotiationAttachmentsGridHeight();

    this.bookingGridOptions = {
      rowHeight: 35,
      suppressCellFocus: true,
      overlayNoRowsTemplate: `<span>${this.translateService.instant('AG_GRID_NO_BOOKINGS')}</span>`,
      overlayComponentParams: {
        noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
      },
    };
    this.bookingGridOptions.pagination = false;
    this.bookingGridOptions.suppressCellFocus = true;
    this.bookingGridOptions.context = {
      credit: 'CREDIT',
      cancelledState: BookingStateEnum.CANCELLED,
    };
    this.bookingGridOptions.rowClass = 'white-row';
    this.bookingGridOptions.rowClassRules = {
      'gray-characters': 'data.bookingStateId === ctx.cancelledState',
      'red-characters': 'data.bookingType === ctx.credit && data.bookingStateId !== ctx.cancelledState',
    };

    this.sidebarHandleService.enableSidebarForView();
    this.decimalSeparator = this.userService.userLanguage.startsWith('en') ? '.' : ',';
    this.userLanguage = this.userService.userLanguage.startsWith('en') ? '' : 'DE';

    this.initValidationMessages();
    this.setFormValues();
    this.setTopicDropdownValues();
    this.initialFormValue = cloneDeep(this.recourseCampaignEditForm.value);
    this.onChanges();

    this.configureGeneralDataColumnDefs();
    this.configureComplaintTableColumnDefs();
    this.configureRecourseClaimTableColumnDefs();
    this.configureRecourseNegotiationTableColumnDefs();
    this.configureBookingTableColumnDefs();
    this.formValueChanged = false;

    this.formValueChanged = false;

    this.defaultColDef = {
      resizable: true,
      sortable: false,
      suppressHeaderMenuButton: true,
    };

    this.multiSortKey = 'ctrl';

    if (!this.isAssignedPersonAvailable) {
      this.onCheckRecourseCampaignEditAssignedPersonEE.emit();
    }
  }

  private getCommonAttachmentsGridOptions(): any {
    return {
      rowHeight: 35,
      suppressCellFocus: true,
      overlayNoRowsTemplate: `<span>${this.translateService.instant('AG_GRID_NO_ATTACHMENTS')}</span>`,
      overlayComponentParams: {
        noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
      },
    };
  }

  private configureBookingTableColumnDefs(): void {
    this.bookingTableColumnDefs = [
      {
        headerName: this.translateService.instant('RECOURSE_CAMPAIGN_DETAIL_TABLE_BOOKING_ID'),
        field: 'pkBookingId',
        sort: 'desc',
      },
      {
        headerName: this.translateService.instant('RECOURSE_CAMPAIGN_DETAIL_TABLEBOOKING_AMOUNT'),
        field: 'amount',
        type: 'rightAligned',
        valueFormatter: this.agGridFormatterService.currencyFormatterDefault(this.userService),
      },
      {
        headerName: this.translateService.instant('RECOURSE_CAMPAIGN_DETAIL_TABLEBOOKING_CREDITOR_NO'),
        field: 'creditorNo',
      },
      {
        headerName: this.translateService.instant('RECOURSE_CAMPAIGN_DETAIL_TABLEBOOKING_CREDITOR'),
        field: 'creditor',
      },
      {
        headerName: this.translateService.instant('RECOURSE_CAMPAIGN_DETAIL_TABLEBOOKING_DUE_DATE'),
        field: 'dueDate',
        valueFormatter: this.agGridFormatterService.dateFormatter,
      },
      {
        headerName: this.translateService.instant('RECOURSE_CAMPAIGN_DETAIL_TABLEBOOKING_BOOKING_TYPE'),
        field: 'bookingType',
        valueGetter: this.agGridService.translateBookingType(this.translateService),
      },
      {
        headerName: this.translateService.instant('RECOURSE_CAMPAIGN_DETAIL_TABLEBOOKING_STATE'),
        valueGetter: this.agGridService.getBookingStateName(this.translateService),
      },
      {
        headerName: '',
        headerClass: ['ag-header-last-cell'],
        minWidth: 50,
        maxWidth: 50,
        field: 'value',
        cellRenderer: 'buttonDetailsRenderer',
        filter: false,
        cellRendererParams: {
          onClick: this.onBtnBookingDetails.bind(this),
        },
        menuTabs: [],
        unSortIcon: false,
      },
    ];
  }

  private configureRecourseNegotiationTableColumnDefs(): void {
    this.recourseNegotiationTableColumnDefs = [
      {
        headerName: this.translateService.instant('ATTACHMENTS_FILE_NAME'),
        field: 'filename',
        sort: 'asc',
      },
      { headerName: this.translateService.instant('ATTACHMENTS_CREATOR'), field: 'creator' },
      { headerName: this.translateService.instant('ATTACHMENTS_DATE'), field: 'createDate' },
      {
        headerName: '',
        field: '',
        cellRenderer: 'downloadCellRenderer',
        cellRendererParams: {
          onDownload: this.onDownload.bind(this),
        },
        filter: false,
        menuTabs: [],
        unSortIcon: false,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
      },
      {
        headerName: '',
        field: '',
        cellRenderer: 'deleteCellRenderer',
        cellRendererParams: {
          onDelete: this.onNegotiationDelete.bind(this),
          isCampaignState8or9: this.isCancelledOrInternalFault,
        },
        filter: false,
        menuTabs: [],
        unSortIcon: false,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
      },
    ];
  }

  private configureRecourseClaimTableColumnDefs(): void {
    this.recourseClaimTableColumnDefs = [
      {
        headerName: this.translateService.instant('ATTACHMENTS_FILE_NAME'),
        field: 'filename',
        sort: 'asc',
      },
      { headerName: this.translateService.instant('ATTACHMENTS_CREATOR'), field: 'creator' },
      { headerName: this.translateService.instant('ATTACHMENTS_DATE'), field: 'createDate' },
      {
        headerName: '',
        field: '',
        cellRenderer: 'downloadCellRenderer',
        cellRendererParams: {
          onDownload: this.onDownload.bind(this),
        },
        filter: false,
        menuTabs: [],
        unSortIcon: false,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
      },
      {
        headerName: '',
        field: '',
        cellRenderer: 'deleteCellRenderer',
        cellRendererParams: {
          onDelete: this.onRecourseClaimDelete.bind(this),
          isCampaignState8or9: this.isCancelledOrInternalFault,
        },
        filter: false,
        menuTabs: [],
        unSortIcon: false,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
      },
    ];
  }

  private configureComplaintTableColumnDefs(): void {
    this.complaintTableColumnDefs = [
      {
        headerName: this.translateService.instant('ATTACHMENTS_FILE_NAME'),
        field: 'filename',
        sort: 'asc',
      },
      { headerName: this.translateService.instant('ATTACHMENTS_CREATOR'), field: 'creator' },
      { headerName: this.translateService.instant('ATTACHMENTS_DATE'), field: 'createDate' },
      {
        headerName: '',
        field: '',
        cellRenderer: 'downloadCellRenderer',
        cellRendererParams: {
          onDownload: this.onDownload.bind(this),
        },
        filter: false,
        menuTabs: [],
        unSortIcon: false,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
      },
      {
        headerName: '',
        field: '',
        cellRenderer: 'deleteCellRenderer',
        cellRendererParams: {
          onDelete: this.onComplaintDelete.bind(this),
          isCampaignState8or9: this.isCancelledOrInternalFault,
        },
        filter: false,
        menuTabs: [],
        unSortIcon: false,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
      },
    ];
  }

  private configureGeneralDataColumnDefs(): void {
    this.generalDataColumnDefs = [
      {
        headerName: this.translateService.instant('ATTACHMENTS_FILE_NAME'),
        field: 'filename',
        sort: 'asc',
      },
      { headerName: this.translateService.instant('ATTACHMENTS_CREATOR'), field: 'creator' },
      { headerName: this.translateService.instant('ATTACHMENTS_DATE'), field: 'createDate' },
      {
        headerName: '',
        field: '',
        cellRenderer: 'downloadCellRenderer',
        cellRendererParams: {
          onDownload: this.onDownload.bind(this),
        },
        filter: false,
        menuTabs: [],
        unSortIcon: false,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
      },
      {
        headerName: '',
        field: '',
        cellRenderer: 'deleteCellRenderer',
        cellRendererParams: {
          onDelete: this.onGeneralDataDelete.bind(this),
          isCampaignState8or9: this.isCancelledOrInternalFault,
        },
        filter: false,
        menuTabs: [],
        unSortIcon: false,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
      },
    ];
  }

  // eslint-disable-next-line complexity
  private async showErrorMessageIfNeeded(key: number): Promise<boolean> {
    const areComplaintAttachmentsOk = await this.areComplaintAttachmentsOk();
    const areRecourseClaimAttachmentsOk = await this.areRecourseClaimAttachmentsOk();
    const areNegotiationAttachmentsOk = await this.areNegotiationAttachmentsOk();

    let translationMessage = '';

    if (key >= 2 && key <= 7) {
      if (!(this.data.publishedDate && this.recourseCampaignEditForm.valid) && 2 <= key) {
        translationMessage = 'CAMPAING_DETAILS_MESSAGE_2';
      } else if (!(this.recourseCampaignEditForm.value.complaintDate && areComplaintAttachmentsOk) && 3 <= key) {
        translationMessage = 'CAMPAING_DETAILS_MESSAGE_3';
      } else if (
        !(this.recourseCampaignEditForm.value.recourseClaimDate && areRecourseClaimAttachmentsOk) &&
        4 <= key
      ) {
        translationMessage = 'CAMPAING_DETAILS_MESSAGE_4';
      } else if (!(this.getNegotiationDate() && areNegotiationAttachmentsOk) && 5 <= key) {
        translationMessage = 'CAMPAING_DETAILS_MESSAGE_5';
      } else if (this.data != null && this.data.bookings != null && this.data.bookings.length === 0 && 6 <= key) {
        translationMessage = 'CAMPAING_DETAILS_MESSAGE_6';
      }
    }
    if (translationMessage) {
      this.translateService
        .get(['CHANGE_OF_STATE', translationMessage, 'OK'])
        .subscribe((translate: { [key: string]: any }) => {
          const title: string = translate.CHANGE_OF_STATE;
          const message: string = translate[translationMessage];
          const ok: string = translate.OK;

          this.pasConfirmationService.showConfirmation(title, message, ok, '', false).then(
            () => {},
            () => {},
          );
        });
      return true;
    }
    return false;
  }

  private getNegotiationDate(): boolean {
    let toReturn = false;
    if (this.data != null && this.data.recourseCampaignNegotiations != null) {
      this.data.recourseCampaignNegotiations.forEach((nm: RecourseCampaignNegotiationModel) => {
        if (nm.negotiationDate) {
          toReturn = true;
        }
      });
    }
    return toReturn;
  }

  private async areComplaintAttachmentsOk(): Promise<boolean> {
    let result = false;
    await this.recourseCampaingStore.getComplaintAttachments().then((model: AttachmentModel[]) => {
      if (model.length > 0) {
        result = true;
      }
    });
    return result;
  }

  private async areRecourseClaimAttachmentsOk(): Promise<boolean> {
    let result = false;
    await this.recourseCampaingStore.getRecourseClaimAttachments().then((model: AttachmentModel[]) => {
      if (model.length > 0) {
        result = true;
      }
    });
    return result;
  }

  private async areNegotiationAttachmentsOk(): Promise<boolean> {
    let result = false;
    await this.recourseCampaingStore.getNegotiationAttachments().then((model: AttachmentModel[]) => {
      if (model.length > 0) {
        result = true;
      }
    });
    return result;
  }

  private async isLastComplaintAttachments(): Promise<boolean> {
    let result = false;
    await this.recourseCampaingStore.getComplaintAttachments().then((model: AttachmentModel[]) => {
      if (model.length === 1) {
        result = true;
      }
    });
    return result;
  }

  private async isLastRecourseClaimAttachments(): Promise<boolean> {
    let result = false;
    await this.recourseCampaingStore.getRecourseClaimAttachments().then((model: AttachmentModel[]) => {
      if (model.length === 1) {
        result = true;
      }
    });
    return result;
  }

  private onDownload(event): void {
    const fileName = event.rowData.filename;
    this.attachmentService.download(event.rowData.id).subscribe({
      next: response => {
        const blob = new Blob([response], { type: response.type });
        fileSaver.saveAs(blob, event.rowData.filename);
      },
      error: () => {
        showAttachmentDownloadFailed(this.notificationServiceWrapper, this.translateService, fileName);
      },
      complete: () => {},
    });
  }

  private onGeneralDataDelete(event): void {
    this.onDelete(event, this.generalDataGridApi);
  }

  private async onComplaintDelete(event): Promise<void> {
    const isLastComplaint = await this.isLastComplaintAttachments();
    const key = Number(this.recourseCampaignEditForm.value.recourseCampaignState.key);
    if (isLastComplaint && key >= 3 && key < 8) {
      this.showErrorMessageAttachments();
    } else {
      this.onDelete(event, this.complaintGridApi);
    }
  }

  private async onRecourseClaimDelete(event): Promise<void> {
    const isLastRecourseClaim = await this.isLastRecourseClaimAttachments();
    const key = Number(this.recourseCampaignEditForm.value.recourseCampaignState.key);
    if (isLastRecourseClaim && key > 3 && key < 8) {
      this.showErrorMessageAttachments();
    } else {
      this.onDelete(event, this.recourseClaimGridApi);
    }
  }

  private onNegotiationDelete(event): void {
    this.onDelete(event, this.negotiationGridApi);
  }

  private showErrorMessageAttachments(): void {
    this.translateService
      .get(['ATTACHMENT_CAN_NOT_BE_SAVED', 'ATTACHMENT_CAN_NOT_BE_SAVED_MESSAGE', 'OK'])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.ATTACHMENT_CAN_NOT_BE_SAVED;
        const message: string = translate.ATTACHMENT_CAN_NOT_BE_SAVED_MESSAGE;
        const ok: string = translate.OK;
        this.pasConfirmationService.showConfirmation(title, message, ok, '', false).then(
          () => {},
          () => {},
        );
      });
  }

  private onDelete(event, gridApi): void {
    this.translateService
      .get(['DELETE_ATTACHMENT_TITLE', 'DELETE_ATTACHMENT_MESSAGE', 'OK', 'CANCEL'])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.DELETE_ATTACHMENT_TITLE;
        const message: string = translate.DELETE_ATTACHMENT_MESSAGE;
        const ok: string = translate.OK;
        const dismiss: string = translate.CANCEL;

        this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, true).then(
          () => {
            this.processComponent.startProcess();
            const fileName = event.rowData.filename;
            this.attachmentService.delete(event.rowData.id).subscribe({
              next: () => {
                this.processComponent.stopProcess();
                showAttachmentDeleteSuccess(this.notificationServiceWrapper, this.translateService, fileName);
              },
              error: () => {
                this.processComponent.stopProcess();
                showAttachmentDeleteFailed(this.notificationServiceWrapper, this.translateService, fileName);
              },
            });
            const deleteItems = [event.rowData];
            gridApi.applyTransaction({ remove: deleteItems });
          },
          () => {},
        );
      });
  }

  private onChanges(): void {
    this.formChangedSubscription = this.recourseCampaignEditForm.valueChanges.subscribe(newFormValue => {
      this.formValueChanged = this.areFormFieldsChanged(this.initialFormValue, newFormValue);
    });
  }

  private areFormFieldsChanged(initialFormValue, newFormValue): boolean {
    let formChanged = false;
    Object.keys(initialFormValue).forEach((key: string) => {
      const initialFieldValue = initialFormValue[key];
      const newFieldValue = newFormValue[key];
      switch (key) {
        case 'costEditDate':
        case 'complaintDate':
        case 'recourseClaimDate': {
          if (!this.areDatesEqual(initialFieldValue, newFieldValue)) {
            formChanged = true;
          }
          break;
        }
        case 'affectedVehicles':
        case 'costsPrognosisInternal':
        case 'recourseActualCostsEur':
        case 'revenueOthersEur': {
          if (!this.areNumericValuesEqual(initialFieldValue, newFieldValue)) {
            formChanged = true;
          }
          break;
        }
        default: {
          if (
            !isEqual(initialFieldValue, newFieldValue) &&
            !this.stringsNullOrEmpty(initialFieldValue, newFieldValue)
          ) {
            formChanged = true;
          }
        }
      }
    });
    return formChanged;
  }

  private areNumericValuesEqual(initValue, newValue): boolean {
    return Number(initValue) === Number(newValue);
  }

  private areDatesEqual(initValue, newValue): boolean {
    return (
      this.dateFormatPipe.transformDateToGlobalFormat(initValue) ===
      this.dateFormatPipe.transformDateToGlobalFormat(newValue)
    );
  }

  private stringsNullOrEmpty(initValue, newValue): boolean {
    return this.isStringNullOrEmpty(initValue) && this.isStringNullOrEmpty(newValue);
  }

  private isStringNullOrEmpty(inputString): boolean {
    return !inputString || inputString.length === 0;
  }

  public onFirstDataRendered(params): void {
    params.api.sizeColumnsToFit();
  }

  /**
   * const gridWidth maybe will be reactivated when the table functionality will be implemented
   */
  public onGridSizeChanged(params): void {
    const columnsToShow = [];
    const columnsToHide = [];
    params.api.setColumnsVisible(columnsToShow, true);
    params.api.setColumnsVisible(columnsToHide, false);
    params.api.sizeColumnsToFit();
  }

  /** At the moment each table has the same e.g. colId
   * because the tables still not working.
   * So, adjustments will be made in the future
   */
  public async onGeneralDataGridReady(params): Promise<void> {
    this.generalDataGridApi = params.api;

    this.generalDataGridApi.setGridOption('rowData', await this.recourseCampaingStore.getGeneralDataAttachments());
    this.setGeneralAttachmentsGridHeight();
  }

  /** In the moment each table has the same e.g. colId
   * because the tables still not working.
   * So, adjustments will be made in the future
   */
  public onBookingGridReady(event: GridReadyEvent): void {
    this.bookingGridApi = event.api;
    this.bookingGridApi.sizeColumnsToFit();

    this.populateBookingsTable();
  }

  private populateBookingsTable(): void {
    this.bookingGridApi.setGridOption('rowData', this.data.bookings);
  }

  /** In the moment each table has the same e.g. colId
   * because the tables still not working.
   * So, adjustments will be made in the future
   */
  public async onNegotiationGridReady(params: any): Promise<void> {
    this.negotiationGridApi = params.api;

    this.negotiationGridApi.setGridOption('rowData', await this.recourseCampaingStore.getNegotiationAttachments());
    this.setNegotiationAttachmentsGridHeight();
  }

  /** In the moment each table has the same e.g. colId
   * because the tables still not working.
   * So, adjustments will be made in the future
   */
  public async onComplaintGridReady(params: any): Promise<void> {
    this.complaintGridApi = params.api;

    this.complaintGridApi.setGridOption('rowData', await this.recourseCampaingStore.getComplaintAttachments());
    this.setComplaintAttachmentsGridHeight();
  }

  /** In the moment each table has the same e.g. colId
   * because the tables still not working.
   * So, adjustments will be made in the future
   */
  public async onRecourseClaimGridReady(params: any): Promise<void> {
    this.recourseClaimGridApi = params.api;

    this.recourseClaimGridApi.setGridOption('rowData', await this.recourseCampaingStore.getRecourseClaimAttachments());
    this.setRecourseClaimAttachmentsGridHeight();
  }

  // eslint-disable-next-line complexity
  private setFormValues(): void {
    this.selectedRecourseCampaignState = this.data.fkCampaignStateId;
    this.isCancelledOrInternalFault =
      this.data.fkCampaignStateId === CampaignStateEnum.CANCELLED_STOPPED_CHANGED ||
      this.data.fkCampaignStateId === CampaignStateEnum.NO_SUPPLIER_FAULT;
    const selectedCampaignStateIndex = this.recourseCampaingStore.recourseCampaignStateListForSelection.findIndex(
      p => p.key === this.data.fkCampaignStateId,
    );
    const selectedUserIndex = this.recourseCampaingStore.userByRightListForSelection.findIndex(
      p => p.key === this.data.assignee,
    );
    if (selectedUserIndex === -1 && this.data.fkCampaignStateId !== 10) {
      this.isAssignedPersonAvailable = false;
    }
    this.recourseCampaignEditForm = this.fb.group({
      campaignRecourseId: [this.data.campaignRecourseId],
      recourseCampaignExId: [this.data.recourseCampaignExId],
      operationId: [this.data.operationId],
      topic: [this.data.topic],
      campaignCreateDate: [this.dateFormatPipe.transformDateToGermanFormat(this.data.campaignCreateDate)],
      businessYear: [this.data.businessYear],
      publishedDate: [this.dateFormatPipe.transformDateToGermanFormat(this.data.publishedDate)],
      affectedVehicles: [this.data.affectedVehicles],
      targetCars: [this.data.targetCars],
      fulfillmentDegree: [
        new DecimalFormatConverter(this.userService.userLanguage as UserLanguage).transformByGivenSeparator(
          this.data.fulfillmentDegree,
        ),
      ],
      campaignCostsPrognosis: [this.data.campaignCostsPrognosis],
      recourseCampaignState: [
        selectedCampaignStateIndex === -1
          ? {
              key: this.recourseCampaingStore.recourseCampaignStateListForSelection[9].key,
              value: this.translateService.instant(
                this.recourseCampaingStore.recourseCampaignStateListForSelection[9].value,
              ),
            }
          : {
              key: this.recourseCampaingStore.recourseCampaignStateListForSelection[selectedCampaignStateIndex].key,
              value: this.translateService.instant(
                this.recourseCampaingStore.recourseCampaignStateListForSelection[selectedCampaignStateIndex].value,
              ),
            },
        Validators.required,
      ],
      assignee: [
        this.data.assignee != null && selectedUserIndex >= 0
          ? {
              key: this.recourseCampaingStore.userByRightListForSelection[selectedUserIndex].key,
              value: this.recourseCampaingStore.userByRightListForSelection[selectedUserIndex].value,
            }
          : null,
        Validators.required,
      ],
      // GENERAL_INFO
      orderType: [this.data.orderType, Validators.required],
      markets: [this.data.markets],
      supplier: [this.data.supplier],
      supplierName: [{ value: this.data.name1, disabled: true }],
      contactInternal: [this.data.contactInternal],
      contactExternal: [this.data.contactExternal],
      recourseCoordination: [this.data.recourseCoordination, Validators.required],
      recourseActualCostsEur: [this.data.recourseActualCostsEur],
      costsPrognosisInternal: [this.data.costsPrognosisInternal],
      costEditDate: [this.data.costEditDate],
      partsRequest: [this.data.partsRequest == null ? '0' : this.data.partsRequest],
      comment: [this.data.comment],
      // COMPLAINT (MANGELRÜGE)
      complaintDate: [this.data.complaintDate],
      // RECOURSECLAIM (REGRESSFORDERUNG)
      recourseClaimDate: [this.data.recourseClaimDate],
      negotiationDate: [''],
      // NEGOTIATIO
      recourseCampaignNegotiations: [this.data.recourseCampaignNegotiations],
      // BOOKING
      totalRevenue: [
        {
          value:
            this.data.recourseBookingSumRevenues == null ? null : this.data.recourseBookingSumRevenues.totalRevenue,
          disabled: true,
        },
      ],
      revenuesCurrentBusinessYear: [
        {
          value:
            this.data.recourseBookingSumRevenues == null
              ? null
              : this.data.recourseBookingSumRevenues.revenuesCurrentBusinessYear,
          disabled: true,
        },
      ],
      revenuesPreviousYears: [
        {
          value:
            this.data.recourseBookingSumRevenues == null
              ? null
              : this.data.recourseBookingSumRevenues.revenuesPreviousYears,
          disabled: true,
        },
      ],
      revenueOthersEur: [this.data.revenueOthersEur],
      outstandingRevenues: [
        {
          value:
            this.data.recourseBookingSumRevenues == null
              ? null
              : this.data.recourseBookingSumRevenues.outstandingRevenues,
          disabled: true,
        },
      ],
    });
    if (this.isCancelledOrInternalFault) {
      this.disableRelevantFormFields();
    }
    this.validateSupplierField();
  }

  public setTopicDropdownValues(): void {
    const enumToArray: string[] = extractEnumValues(TopicEnum);
    this.topicDropdownValues = getDropdownValues(enumToArray, this.translateService);
  }

  private initValidationMessages(): void {
    this.validationMessages = [
      {
        /*00*/ error: 'required',
        message: this.translateService.instant('VALIDATION_INPUT_REQUIRED'),
      },
    ];
  }

  public onSupplierChanged(): void {
    const supplierControl = this.recourseCampaignEditForm.controls.supplier;
    const supplierValue: string = supplierControl.value;
    if (isEmpty(supplierValue) || supplierValue.length < 6) {
      this.recourseCampaignEditForm.controls.supplierName.setValue('');
    } else {
      this.supplierService.getSupplier({ supplierId: supplierValue }).subscribe({
        next: supplier => {
          if (supplier?.name) {
            this.recourseCampaignEditForm.controls.supplierName.setValue(supplier.name);
          } else {
            supplierControl.setErrors({ incorrect: true });
            this.recourseCampaignEditForm.controls.supplierName.setValue('');
          }
        },
        error: () => {
          supplierControl.setErrors({ incorrect: true });
          this.recourseCampaignEditForm.controls.supplierName.setValue('');
        },
      });
    }
  }

  public isRecourseCampaignEditSaveEnabled(): boolean {
    return this.recourseCampaignEditForm?.dirty && this.recourseCampaignEditForm?.valid && this.formValueChanged;
  }

  public async onBtnSave(): Promise<void> {
    this.processComponent.startProcess();
    // Important to switch state between 8 and 9!
    this.enableRelevantFormFields();
    // Selected date on DatePicker that has not yet been assigned to the list should also be taken into account
    this.addNegotiationDate(this.recourseCampaignEditForm.get('negotiationDate').value);
    const key = Number(this.recourseCampaignEditForm.value.recourseCampaignState.key);
    if (!(await this.showErrorMessageIfNeeded(key))) {
      const retVal: boolean = await this.recourseCampaingStore.saveRecourseCampaignDetails(
        this.recourseCampaignEditForm.value,
      );
      if (retVal) {
        this.isAssignedPersonAvailable = true;
        this.initializeView();
      }
    }
    this.processComponent.stopProcess();
  }

  public onBtnCancel(): void {
    this.onBtnRecourseCampaignEditCancelEE.emit();
  }

  public onBtnCreateManualBooking(): void {
    const supplierData: SupplierModel = new SupplierModel();
    supplierData.supplier = this.data.supplier;
    const newBookingModel: RecourseBookingModel = new RecourseBookingModel();
    newBookingModel.supplierData = supplierData;
    newBookingModel.recourseId = this.data.recourseCampaignExId ?? this.data.campaignRecourseId;
    newBookingModel.bookingType = BookingTypeEnum.DEBIT;
    newBookingModel.recourseTypeId = RecourseTypeIdEnum.CAMPAIGN;

    this.recourseBookingStore.setRecourseId(this.data.campaignRecourseId);
    this.recourseBookingStore.setRecourseCampaignExId(this.data.recourseCampaignExId);

    this.bookingRoutingService.createNewBooking(newBookingModel, '/recourseCampaign');
  }

  public onBtnBookingDetails(event): void {
    this.bookingRoutingService.openBookingDetailsView(event.rowData.pkBookingId);
  }

  private addNegotiationDate(dateValAsString: string): void {
    if (!dateValAsString) {
      return;
    }
    if (this.data.recourseCampaignNegotiations == null) {
      this.data.recourseCampaignNegotiations = [];
    }
    this.data.recourseCampaignNegotiations.push(new RecourseCampaignNegotiationModel(new Date(dateValAsString)));
    this.selectedNegotiationDate = null;
    this.formValueChanged = true;
  }

  public deleteNegotiationDate(dateVal: Date): void {
    this.data.recourseCampaignNegotiations = this.data.recourseCampaignNegotiations.filter(
      item => item.negotiationDate !== dateVal,
    );
    if (this.selectedNegotiationDate != null && this.selectedNegotiationDate.negotiationDate === dateVal) {
      this.selectedNegotiationDate = null;
    }
    this.formValueChanged = JSON.stringify(this.data.recourseCampaignNegotiations) !== this.defaultNegotiationDates;
  }

  public onSelectNegotiationDate(newSelVal: RecourseCampaignNegotiationModel): void {
    this.selectedNegotiationDate = newSelVal;
  }

  public onSelectTopic(value: any): void {
    this.uploadFileModel.topic = value;
    this.acceptanceAllowed = this.checkUploadState();
  }

  public campaignStateCancelledOrInternalFault(): boolean {
    return this.isCancelledOrInternalFault;
  }

  // Visibility dependence on user-rights
  public isCreateManualBookingVisible(): boolean {
    return this.recourseCampaingStore.userRights.includes(UserRights.CAMPAIGNS_BOOKING);
  }

  // Button 'Manuelle Buchung anlegen' should be inactive as long as mandatory fields of campaign are not filled and saved
  public isCreateManualBookingEnabled(): boolean {
    return (
      !this.isRecourseCampaignEditSaveEnabled() &&
      this.recourseCampaignEditForm.valid &&
      this.isRecourseCampaignStateOk() &&
      !this.isCancelledOrInternalFault
    );
  }

  // Button for uploading attachments
  public isAttachmentButtonEnabled(): boolean {
    return !this.isCancelledOrInternalFault;
  }

  public isRecourseCampaignStateOk(): boolean {
    const recourseCampaignStateId = Number(this.data.recourseCampaignStateId);
    return (
      recourseCampaignStateId === CampaignStateEnum.NEGOTIATED ||
      recourseCampaignStateId === CampaignStateEnum.DUE_DATE_REVENUES ||
      recourseCampaignStateId === CampaignStateEnum.COMPLETELY_BOOKED
    );
  }

  public canUserUploadAttachments(): boolean {
    return this.recourseCampaingStore.userRights.includes(UserRights.CAMPAIGNS_BOOKING);
  }

  public onFileInput(event: any): void {
    this.uploadFileModel.fileToUpload = event.target.files.item(0);
    if (this.uploadFileModel.fileToUpload.type === 'application/pdf') {
      this.externalReceiverCheckboxDisabled = false;
    } else {
      this.uploadFileModel.extern = false; // set checkbox state to unchecked
      this.externalReceiverCheckboxDisabled = true;
    }
    this.uploadFileModel.filename = this.uploadFileModel.fileToUpload.name;
    this.acceptanceAllowed = this.checkUploadState();
  }

  public onUploadAttachments(): void {
    // open upload dialog window
    this.acceptanceAllowed = this.checkUploadState();
    this.uploadFileDialog = this.modalService.open(this.uploadFileDialogTemplate, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
    });

    // this handles actions after dialog is closed
    this.uploadFileDialog.result.then(() => {
      this.onCloseUploadFileDialog();
      this.uploadFileModel.reset();
    });
  }

  public onAcceptUploadFileDialog(): void {
    this.uploadFileDialog.close();
    const currentTopic = cloneDeep(this.uploadFileModel.topic.key);
    const formData = newFormDataForAttachment(
      this.uploadFileModel,
      this.uploadFileModel.topic.key,
      [],
      this.data.campaignRecourseId,
      this.getCampaignExId(),
    );
    this.processComponent.startProcess();

    this.attachmentService.upload(formData).subscribe({
      next: result => {
        this.processComponent.stopProcess();
        showAttachmentUploadSuccess(this.notificationServiceWrapper, this.translateService, formData.get('filename'));
        if (getEnumStringValue(TopicEnum, 0) === currentTopic) {
          this.generalDataGridApi.applyTransaction({ add: result });
        }
        if (getEnumStringValue(TopicEnum, 1) === currentTopic) {
          this.complaintGridApi.applyTransaction({ add: result });
        }
        if (getEnumStringValue(TopicEnum, 2) === currentTopic) {
          this.recourseClaimGridApi.applyTransaction({ add: result });
        }
        if (getEnumStringValue(TopicEnum, 3) === currentTopic) {
          this.negotiationGridApi.applyTransaction({ add: result });
        }
      },
      error: err => {
        this.processComponent.stopProcess();
        showAttachmentUploadFailed(
          this.notificationServiceWrapper,
          this.translateService,
          formData.get('filename'),
          err,
        );
      },
    });
  }

  private getCampaignExId(): string {
    if (this.data.recourseCampaignExId) {
      return this.data.recourseCampaignExId;
    }
    return '';
  }

  public onCloseUploadFileDialog(): void {
    this.uploadFileDialog.close();
    this.uploadFileModel.reset();
  }

  public onCheckboxChanged(): void {
    this.acceptanceAllowed = this.checkUploadState();
  }

  public checkUploadState(): boolean {
    // Only files <= 15MB are allowed
    return (
      this.isTopicSelected() &&
      (isNil(this.uploadFileModel.fileToUpload)
        ? false
        : this.uploadFileModel.fileToUpload.size <= this.maxFileUploadSize)
    );
  }

  public isTopicSelected(): boolean {
    return !isNil(this.uploadFileModel.topic) && this.uploadFileModel.topic.key !== '';
  }

  public isTopicVisible(): boolean {
    return true;
  }

  public async forwardToQpp(): Promise<void> {
    const url: string = await this.recourseCampaingStore.getQppSysReserveUrl();
    // eslint-disable-next-line no-restricted-globals
    const win = window.open(url, '_blank');
    win.focus();
  }

  public userHasWorkflowRight(): boolean {
    return this.recourseCampaingStore.userRights.includes(UserRights.CAMPAIGNS_WORKFLOW);
  }

  public onDuplicateCampaignClicked(): void {
    // open dialog window
    this.operationId = this.data.operationId;
    this.duplicateCampaignDialog = this.modalService.open(this.duplicateCampaignDialogTemplate, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
    });
    // handling actions after dialog is closed
    this.duplicateCampaignDialog.result.then(() => {
      this.onCloseDuplicateCampaignDialog();
    });
  }

  public onAcceptDuplicateCampaignDialog(): void {
    this.spinnerActive = true;

    this.recourseCampaignService.duplicateRecourseCampaign(this.createDuplicateCampaign()).subscribe({
      next: savedDuplicateCampaign => {
        const title: string = this.translateService.instant('DUPLICATE_CAMPAIGN_SUCCESS_TITLE', {
          0: this.data.campaignRecourseId,
        });
        const content: string = this.translateService.instant('DUPLICATE_CAMPAIGN_SUCCESS_MESSAGE');
        this.notificationServiceWrapper.addSuccessNotification(title, content);
        this.formChangedSubscription.unsubscribe();
        this.recourseCampaignEditForm.controls.supplier.clearAsyncValidators();
        this.recourseCampaignEditForm.enable();
        this.recourseCampaingStore.selectedRecourseCampaignEdit = savedDuplicateCampaign;
        this.initializeView();
        this.populateBookingsTable();
      },
      error: () => {
        const title: string = this.translateService.instant('DUPLICATE_CAMPAIGN_ERROR_TITLE', {
          0: this.data.campaignRecourseId,
        });
        const content: string = this.translateService.instant('DUPLICATE_CAMPAIGN_ERROR_MESSAGE');
        this.notificationServiceWrapper.addErrorNotification(title, content);
        this.onCloseDuplicateCampaignDialog();
      },
      complete: () => {
        this.onCloseDuplicateCampaignDialog();
        this.onChanges();
      },
    });
  }

  public onCloseDuplicateCampaignDialog(): void {
    this.spinnerActive = false;
    this.duplicateCampaignDialog.close();
  }

  private createDuplicateCampaign(): RecourseCampaignDetailsModel {
    const duplicateCampaign = new RecourseCampaignDetailsModel();
    duplicateCampaign.campaignRecourseId = this.data.campaignRecourseId;
    duplicateCampaign.fkCampaignStateId = CampaignStateEnum.NEW_CAMPAIGN;
    duplicateCampaign.operationId = this.operationId;
    duplicateCampaign.orderType = this.data.orderType;
    duplicateCampaign.partsRequest = '0';
    duplicateCampaign.affectedVehicles = this.data.affectedVehicles;
    duplicateCampaign.assignee = this.data.assignee;
    duplicateCampaign.recourseCampaignNegotiations = this.data.recourseCampaignNegotiations;
    duplicateCampaign.recourseBookingSumRevenues = this.data.recourseBookingSumRevenues;
    return duplicateCampaign;
  }

  public onSelectRecourseCampaignState(dropDownValue: any): void {
    this.onChanges();
    this.selectedRecourseCampaignState = dropDownValue.key;
    if (
      CampaignStateEnum.CANCELLED_STOPPED_CHANGED === this.selectedRecourseCampaignState ||
      CampaignStateEnum.NO_SUPPLIER_FAULT === this.selectedRecourseCampaignState
    ) {
      this.isCancelledOrInternalFault = true;
    } else {
      this.isCancelledOrInternalFault = false;
      this.enableRelevantFormFields();
    }
    this.validateSupplierField();
  }

  private enableRelevantFormFields(): void {
    this.recourseCampaignEditForm.controls.assignee.enable();
    this.recourseCampaignEditForm.controls.orderType.enable();
    this.recourseCampaignEditForm.controls.markets.enable();
    this.recourseCampaignEditForm.controls.affectedVehicles.enable();
    this.recourseCampaignEditForm.controls.supplier.enable();
    this.recourseCampaignEditForm.controls.contactInternal.enable();
    this.recourseCampaignEditForm.controls.contactExternal.enable();
    this.recourseCampaignEditForm.controls.recourseCoordination.enable();
    this.recourseCampaignEditForm.controls.recourseActualCostsEur.enable();
    this.recourseCampaignEditForm.controls.costsPrognosisInternal.enable();
    this.recourseCampaignEditForm.controls.costEditDate.enable();
    this.recourseCampaignEditForm.controls.partsRequest.enable();
    this.recourseCampaignEditForm.controls.comment.enable();
    this.recourseCampaignEditForm.controls.complaintDate.enable();
    this.recourseCampaignEditForm.controls.recourseClaimDate.enable();
    this.recourseCampaignEditForm.controls.negotiationDate.enable();
    this.recourseCampaignEditForm.controls.revenueOthersEur.enable();
  }

  private disableRelevantFormFields(): void {
    this.recourseCampaignEditForm.controls.assignee.disable();
    this.recourseCampaignEditForm.controls.orderType.disable();
    this.recourseCampaignEditForm.controls.markets.disable();
    this.recourseCampaignEditForm.controls.affectedVehicles.disable();
    this.recourseCampaignEditForm.controls.supplier.disable();
    this.recourseCampaignEditForm.controls.contactInternal.disable();
    this.recourseCampaignEditForm.controls.contactExternal.disable();
    this.recourseCampaignEditForm.controls.recourseCoordination.disable();
    this.recourseCampaignEditForm.controls.recourseActualCostsEur.disable();
    this.recourseCampaignEditForm.controls.costsPrognosisInternal.disable();
    this.recourseCampaignEditForm.controls.costEditDate.disable();
    this.recourseCampaignEditForm.controls.partsRequest.disable();
    this.recourseCampaignEditForm.controls.comment.disable();
    this.recourseCampaignEditForm.controls.complaintDate.disable();
    this.recourseCampaignEditForm.controls.recourseClaimDate.disable();
    this.recourseCampaignEditForm.controls.negotiationDate.disable();
    this.recourseCampaignEditForm.controls.revenueOthersEur.disable();
  }

  public validateSupplierField(): void {
    const creditorNo: AbstractControl = this.recourseCampaignEditForm.controls.supplier;

    const key = Number(this.selectedRecourseCampaignState);
    if (
      key === CampaignStateEnum.NEW_CAMPAIGN ||
      key === CampaignStateEnum.PREPARE_DATA ||
      key === CampaignStateEnum.PUBLISH_DATE ||
      key === CampaignStateEnum.CANCELLED_STOPPED_CHANGED ||
      key === CampaignStateEnum.NO_SUPPLIER_FAULT
    ) {
      creditorNo.clearValidators();
      creditorNo.setErrors(null);
      creditorNo.setValidators([Validators.pattern(/^\*$|^\d{6}$/)]);
      creditorNo.updateValueAndValidity();
    } else {
      creditorNo.setValidators([Validators.required, Validators.pattern(/^\*$|^\d{6}$/)]);
      creditorNo.updateValueAndValidity();
    }
  }

  private setGeneralAttachmentsGridHeight(): void {
    setGridHeightConditionally(this.generalDataGridApi, '#generalDataTable');
  }

  private setComplaintAttachmentsGridHeight(): void {
    setGridHeightConditionally(this.complaintGridApi, '#complaintTable');
  }

  private setRecourseClaimAttachmentsGridHeight(): void {
    setGridHeightConditionally(this.recourseClaimGridApi, '#recourseClaimTable');
  }

  private setNegotiationAttachmentsGridHeight(): void {
    setGridHeightConditionally(this.negotiationGridApi, '#recourseNegotiationTable');
  }
}
